import { createColumnHelper } from "@tanstack/react-table";
import { useCallback, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import type { UnpackResponse } from "../../../api/src/lib";
import type { Transaction } from "../../../api/src/transactions/lib";
import type { TransactionsController } from "../../../api/src/transactions/transactions.controller";
import TabContainerWithTabs from "../components/TabContainer";
import TransactionsTable, {
  useAllTransactions,
  type TransactionsExportState,
} from "../components/TransactionsTable";
import { useAuthenticatedQuery } from "../lib/api";
import { processFileResponse } from "../lib/file";
import DashboardNav from "./DashboardNav";

/**
 * The firm's transactions, from every page of `GET /v1/transactions`
 * (APP-1652). The export (`/transactions/export`) is not admitted and stays
 * unversioned.
 */
export function useQueryTransactions() {
  return useAllTransactions("/v1/transactions");
}

/**
 * `/transactions/export` with the state of the table (APP-2159): the sweep
 * switch always, and each column filter only with a value. Type and Sub Type
 * go as labels, repeated for each one.
 */
export function transactionsExportUrl({
  excludeSweep,
  filters,
}: TransactionsExportState) {
  const params = new URLSearchParams({ "exclude-sweep": String(excludeSweep) });
  if (filters.household) params.append("household", filters.household);
  filters.type.forEach((label) => params.append("type", label));
  filters.subType.forEach((label) => params.append("sub-type", label));
  if (filters.symbol) params.append("symbol", filters.symbol);
  if (filters.description) params.append("description", filters.description);
  return `/transactions/export?${params.toString()}`;
}

export function useQueryExportTransactions() {
  // Read when the request is built, so the key (and the cache) stays one.
  const url = useRef(
    transactionsExportUrl({
      excludeSweep: false,
      filters: { type: [], subType: [] },
    }),
  );
  const query = useAuthenticatedQuery<
    UnpackResponse<TransactionsController["exportTransactions"]>,
    void,
    [string, string]
  >({
    queryKey: ["transactions", "export"],
    urlBuilderFn: () => url.current,
    queryFn: async () => undefined,
    queryOptions: { enabled: false },
    responseCallback: processFileResponse,
  });
  const { refetch } = query;
  const exportWith = useCallback(
    (state: TransactionsExportState) => {
      url.current = transactionsExportUrl(state);
      return refetch();
    },
    [refetch],
  );
  return { query, exportWith };
}

type TransactionRow = Transaction;

const TransactionSummary = () => {
  const columnHelper = useMemo(() => createColumnHelper<TransactionRow>(), []);

  const columns = useMemo(
    () => [
      columnHelper.accessor("accountNumber", {
        cell: (info) => (
          <Link to={`/clients/accounts/${info.row.original.accountId}`}>
            {info.getValue()}
          </Link>
        ),
        header: () => "Account Number",
        enableColumnFilter: false,
      }),
      columnHelper.accessor((row) => row.householdName ?? "", {
        id: "householdName",
        cell: (info) =>
          info.row.original.householdId === null ? (
            info.getValue()
          ) : (
            <Link to={`/clients/households/${info.row.original.householdId}`}>
              {info.getValue()}
            </Link>
          ),
        header: () => "Household",
        minSize: 225,
      }),
    ],
    [columnHelper],
  );

  const {
    isPending,
    isError,
    data: transactions,
    retry,
  } = useQueryTransactions();

  const { query: queryExport, exportWith } = useQueryExportTransactions();

  return (
    <TabContainerWithTabs tabs={DashboardNav}>
      <TransactionsTable
        additionalColumns={columns}
        isLoading={isPending}
        isError={isError}
        onRetry={retry}
        transactions={transactions ?? []}
        queryExport={queryExport}
        onExport={exportWith}
      />
    </TabContainerWithTabs>
  );
};

export default TransactionSummary;
